import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutContent } from '@/mocks/about';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${aboutContent.brandName}`,
    description: aboutContent.introduction,
    url: 'https://mhowiub.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: aboutContent.brandName,
      description: aboutContent.introduction,
      url: 'https://mhowiub.cn',
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutContent.contact.email,
        telephone: aboutContent.contact.phone,
        contactType: 'customer service',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutContent.contact.address,
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSubmitted(false);

    try {
      const formEl = e.currentTarget;
      const honeypot = (formEl.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
      if (honeypot) {
        setFormSubmitted(true);
        setFormLoading(false);
        return;
      }

      const formUrl = 'https://readdy.ai/api/form/d9pn28ti2vhsdh2d37mg';
      const formData = new FormData(formEl);
      formData.delete('phone_alt');

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        formEl.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '提交失败，请稍后重试';
      setFormError(message);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />

              {/* Hero */}
              <section className="bg-background-100 rounded-2xl p-10 md:p-14 mb-14 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                  <i className="ri-heart-fill text-2xl"></i>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  关于{aboutContent.brandName}
                </h1>
                <p className="font-heading text-lg md:text-xl text-primary-600 font-semibold mb-4">
                  {aboutContent.tagline}
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                  {aboutContent.introduction}
                </p>
              </section>

              {/* Mission & Story */}
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
                <div className="bg-background-100 rounded-2xl p-8 md:p-10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-focus-3-line text-lg"></i>
                  </div>
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">我们的使命</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {aboutContent.mission}
                  </p>
                </div>
                <div className="bg-background-100 rounded-2xl p-8 md:p-10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-book-open-line text-lg"></i>
                  </div>
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">品牌故事</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {aboutContent.story}
                  </p>
                </div>
              </section>

              {/* Values */}
              <section className="mb-14">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-10">
                  我们的价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {aboutContent.values.map((value, index) => (
                    <div key={index} className="bg-background-100 rounded-xl p-6 text-center">
                      <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                        <i className="ri-star-line text-lg"></i>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                        {value.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Testimonials */}
              <section className="mb-14">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-10">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {aboutContent.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-background-100 rounded-xl p-6">
                      <div className="flex items-center gap-1 mb-3 text-accent-500">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        &ldquo;{testimonial.content}&rdquo;
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
                          {testimonial.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{testimonial.name}</p>
                          <p className="text-xs text-foreground-500">{testimonial.marriageYears}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section className="bg-background-100 rounded-2xl p-8 md:p-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-8">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                      如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或填写右侧的表单，我们会尽快与您取得联系。
                    </p>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">邮箱</p>
                          <p className="text-sm text-foreground-900">{aboutContent.contact.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-wechat-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">微信</p>
                          <p className="text-sm text-foreground-900">{aboutContent.contact.wechat}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">地址</p>
                          <p className="text-sm text-foreground-900">{aboutContent.contact.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">电话</p>
                          <p className="text-sm text-foreground-900">{aboutContent.contact.phone}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    {formSubmitted ? (
                      <div className="bg-background-50 rounded-xl p-8 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                          <i className="ri-check-line text-xl"></i>
                        </div>
                        <p className="text-foreground-900 font-medium">感谢您的留言！</p>
                        <p className="text-sm text-foreground-500 mt-1">我们会尽快与您取得联系。</p>
                      </div>
                    ) : (
                      <form
                        onSubmit={handleSubmit}
                        data-readdy-form
                        className="bg-background-50 rounded-xl p-6"
                      >
                        <div className="flex flex-col gap-4">
                          <div>
                            <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              姓名
                            </label>
                            <input
                              id="contact-name"
                              name="name"
                              type="text"
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              邮箱
                            </label>
                            <input
                              id="contact-email"
                              name="email"
                              type="email"
                              required
                              className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                              留言
                            </label>
                            <textarea
                              id="contact-message"
                              name="message"
                              required
                              rows={4}
                              maxLength={500}
                              className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                              placeholder="请输入您的留言（最多500字）"
                            />
                          </div>

                          <input
                            type="text"
                            name="phone_alt"
                            className="honeypot-field"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                          />

                          {formError && (
                            <p className="text-xs text-red-600">{formError}</p>
                          )}

                          <button
                            type="submit"
                            disabled={formLoading}
                            className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer border-none disabled:opacity-60"
                          >
                            {formLoading ? '提交中...' : '发送留言'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}